import { z } from "zod";
import { IsoDateTimeSchema, StableIdSchema } from "./common.js";

const CronNameSchema = z.string().trim().min(1).max(160);
const CronScheduleInputSchema = z.string().trim().min(1).max(200);
const CronPromptSchema = z.string().max(100_000);
const CronProviderSchema = z.string().trim().min(1).max(100);
const CronModelSchema = z.string().trim().min(1).max(240);
const CronSkillSchema = z
  .string()
  .trim()
  .min(1)
  .max(120)
  .regex(/^[A-Za-z0-9][A-Za-z0-9._-]*$/, "Skill 名称不合法");
const CronDeliveryTargetIdSchema = z
  .string()
  .trim()
  .min(1)
  .max(80)
  .regex(/^[A-Za-z0-9][A-Za-z0-9._-]*$/, "投递目标不合法");

export const CronLevelSchema = z.enum(["system", "agent", "channel"]);
export const CronJobStateSchema = z.enum(["scheduled", "running", "paused", "unknown"]);
export const CronLastResultSchema = z.enum(["none", "running", "succeeded", "failed", "unknown"]);

export const CronScheduleSchema = z
  .object({
    kind: z.enum(["cron", "interval", "once", "systemd", "unknown"]),
    expression: z.string().max(200),
    display: z.string().min(1).max(240),
  })
  .strict();

export const CronDeliverySchema = z
  .object({
    mode: z.enum(["local", "channel", "system"]),
    label: z.string().min(1).max(160),
    /** 仅为 Hermes 返回的平台键（如 qqbot），绝不包含聊天 ID。 */
    targetId: CronDeliveryTargetIdSchema.nullable(),
  })
  .strict();

export const CronJobSchema = z
  .object({
    jobId: StableIdSchema,
    name: CronNameSchema,
    level: CronLevelSchema,
    source: z.enum(["hermes", "systemd"]),
    editable: z.boolean(),
    revision: StableIdSchema.nullable(),
    state: CronJobStateSchema,
    enabled: z.boolean(),
    schedule: CronScheduleSchema,
    action: z
      .object({
        kind: z.enum(["agent_prompt", "script", "system_timer"]),
        summary: z.string().min(1).max(300),
        prompt: CronPromptSchema.nullable(),
        scriptConfigured: z.boolean(),
      })
      .strict(),
    delivery: CronDeliverySchema,
    skills: z.array(CronSkillSchema).max(50),
    model: CronModelSchema.nullable(),
    provider: CronProviderSchema.nullable(),
    noAgent: z.boolean(),
    lastResult: CronLastResultSchema,
    lastRunAt: IsoDateTimeSchema.nullable(),
    nextRunAt: IsoDateTimeSchema.nullable(),
    lastError: z.string().min(1).max(240).nullable(),
    runCount: z.number().int().nonnegative(),
    createdAt: IsoDateTimeSchema.nullable(),
  })
  .strict();

export const CronDeliveryTargetSchema = z
  .object({
    targetId: CronDeliveryTargetIdSchema,
    label: z.string().trim().min(1).max(120),
    homeTargetSet: z.boolean(),
  })
  .strict();

export const CronCatalogSchema = z
  .object({
    jobs: z.array(CronJobSchema).max(1_000),
    deliveryTargets: z.array(CronDeliveryTargetSchema).max(100),
    systemSourceAvailable: z.boolean(),
    updatedAt: IsoDateTimeSchema,
  })
  .strict();

export const CronRunSchema = z
  .object({
    runId: StableIdSchema,
    /** Hermes Stored Session ID；仅用于把运行记录定位到 HerSkin 已同步会话。 */
    agentSessionId: StableIdSchema.nullable(),
    status: z.enum(["running", "completed", "failed", "unknown"]),
    startedAt: IsoDateTimeSchema.nullable(),
    endedAt: IsoDateTimeSchema.nullable(),
    /** 仅在 Hermes 同时给出合法开始/结束时间时计算，不用当前时间猜测运行中耗时。 */
    durationMs: z.number().int().nonnegative().nullable(),
    messageCount: z.number().int().nonnegative(),
  })
  .strict();

export const CronRunPageSchema = z
  .object({
    jobId: StableIdSchema,
    runs: z.array(CronRunSchema).max(100),
    updatedAt: IsoDateTimeSchema,
  })
  .strict();

/**
 * Cron 运行记录就是 Hermes 的 Stored Session。消息查看器只接收正文、角色与时间，
 * 不把 tool_calls、reasoning、session_id 或数据库行字段暴露给浏览器。
 */
export const CronRunMessageSchema = z
  .object({
    messageId: StableIdSchema,
    role: z.enum(["user", "assistant", "system", "tool", "other"]),
    content: z.string().max(200_000),
    createdAt: IsoDateTimeSchema.nullable(),
  })
  .strict();

export const CronRunMessagePageSchema = z
  .object({
    jobId: StableIdSchema,
    runId: StableIdSchema,
    recordedMessageCount: z.number().int().nonnegative(),
    messages: z.array(CronRunMessageSchema).max(500),
    truncated: z.boolean(),
    updatedAt: IsoDateTimeSchema,
  })
  .strict();

export const CreateCronJobRequestSchema = z
  .object({
    name: CronNameSchema,
    schedule: CronScheduleInputSchema,
    level: z.enum(["agent", "channel"]),
    prompt: CronPromptSchema,
    deliveryTarget: CronDeliveryTargetIdSchema,
    skills: z.array(CronSkillSchema).max(20).default([]),
    model: CronModelSchema.nullable().default(null),
    provider: CronProviderSchema.nullable().default(null),
  })
  .strict()
  .superRefine((value, context) => {
    if (!value.prompt.trim()) {
      context.addIssue({ code: "custom", path: ["prompt"], message: "任务内容不能为空" });
    }
    if (value.level === "agent" && value.deliveryTarget !== "local") {
      context.addIssue({
        code: "custom",
        path: ["deliveryTarget"],
        message: "Agent Cron 只能保存到本地",
      });
    }
    if (value.level === "channel" && value.deliveryTarget === "local") {
      context.addIssue({
        code: "custom",
        path: ["deliveryTarget"],
        message: "Channel Cron 必须选择消息通道",
      });
    }
  });

export const UpdateCronJobRequestSchema = z
  .object({
    revision: StableIdSchema,
    name: CronNameSchema,
    schedule: CronScheduleInputSchema,
    prompt: CronPromptSchema.nullable(),
    deliveryTarget: CronDeliveryTargetIdSchema,
    skills: z.array(CronSkillSchema).max(20),
    model: CronModelSchema.nullable(),
    provider: CronProviderSchema.nullable(),
  })
  .strict();

export const CronJobRevisionRequestSchema = z.object({ revision: StableIdSchema }).strict();

export const RunCronJobRequestSchema = z
  .object({
    commandId: StableIdSchema,
    revision: StableIdSchema,
    confirm: z.literal("run_cron_job"),
  })
  .strict();

export const CronTriggerResponseSchema = z
  .object({
    commandId: StableIdSchema,
    job: CronJobSchema,
    state: z.enum(["accepted", "unknown"]),
    idempotent: z.boolean(),
    requestedAt: IsoDateTimeSchema,
  })
  .strict();

export const DeleteCronJobRequestSchema = z
  .object({ revision: StableIdSchema, confirm: z.literal("delete_cron_job") })
  .strict();

export const SystemCronTimerSchema = z
  .object({
    unit: z
      .string()
      .min(1)
      .max(160)
      .regex(/^[A-Za-z0-9@_.:-]+\.timer$/, "systemd timer 名称不合法"),
    activates: z
      .string()
      .min(1)
      .max(160)
      .regex(/^[A-Za-z0-9@_.:-]+\.service$/, "systemd service 名称不合法"),
    nextAt: IsoDateTimeSchema.nullable(),
    lastAt: IsoDateTimeSchema.nullable(),
  })
  .strict();

export const SystemCronSnapshotSchema = z
  .object({
    timers: z.array(SystemCronTimerSchema).max(500),
    updatedAt: IsoDateTimeSchema,
  })
  .strict();

export type CronLevel = z.infer<typeof CronLevelSchema>;
export type CronJob = z.infer<typeof CronJobSchema>;
export type CronCatalog = z.infer<typeof CronCatalogSchema>;
export type CronRunPage = z.infer<typeof CronRunPageSchema>;
export type CronRunMessagePage = z.infer<typeof CronRunMessagePageSchema>;
export type CreateCronJobRequest = z.infer<typeof CreateCronJobRequestSchema>;
export type UpdateCronJobRequest = z.infer<typeof UpdateCronJobRequestSchema>;
export type CronJobRevisionRequest = z.infer<typeof CronJobRevisionRequestSchema>;
export type RunCronJobRequest = z.infer<typeof RunCronJobRequestSchema>;
export type CronTriggerResponse = z.infer<typeof CronTriggerResponseSchema>;
export type DeleteCronJobRequest = z.infer<typeof DeleteCronJobRequestSchema>;
export type SystemCronSnapshot = z.infer<typeof SystemCronSnapshotSchema>;
